// src/pages/CollaboratorGate.jsx
// Shown to guests and talent users.
// Real collaborators are routed directly to CollaboratorPortal via App.jsx.
import { useNavigate } from 'react-router-dom';
import { useUIStore }  from '../store/uiStore';
import { useAuth }     from '../hooks/useAuth';
import Footer          from '../components/layout/Footer';
import { C }           from '../styles/tokens';

const PERKS = [
  { icon: '🤖', title: 'AI-Ranked Talent',      desc: 'Every applicant is scored and ranked by our AI engine — you see the best matches first.' },
  { icon: '🌍', title: 'Global Talent Pool',    desc: 'Access verified professionals from 180+ countries, all KYC-checked and ready.' },
  { icon: '🪙', title: 'Instant ONT Payments',  desc: 'Pay talent directly via Solana blockchain — zero delays, zero intermediaries.' },
  { icon: '📋', title: 'Applicant Tracking',    desc: 'Manage your full hiring pipeline from posting to offer in one place.' },
  { icon: '🔐', title: 'KYC-Verified Profiles', desc: 'Every talent is identity-verified. Hire with confidence.' },
  { icon: '⚡', title: 'Fast Approvals',         desc: 'Post a job and go live within 2–4 hours after admin review.' },
];

export default function CollaboratorGate() {
  const navigate          = useNavigate();
  const { openAuthModal } = useUIStore();
  const { user, profile } = useAuth();

  // Talent users who land here — show a clear "this isn't for you" message
  const isTalent = user && profile?.role === 'talent';

  return (
    <div style={{ background: C.bg, paddingTop: 64 }}>

      {/* Hero */}
      <div style={{ background: `linear-gradient(180deg,${C.navy}70,transparent)`, padding: 'clamp(48px,8vw,96px) 0 clamp(32px,5vw,56px)', position: 'relative', overflow: 'hidden' }}>
        <div className="grid-bg" style={{ opacity: 0.35 }} />
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: 'clamp(180px,30vw,340px)', height: 'clamp(180px,30vw,340px)', borderRadius: '50%', background: `radial-gradient(circle,${C.violet}30,transparent 70%)`, pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 760, margin: '0 auto', padding: '0 clamp(16px,4vw,28px)' }}>
          <span className="pill" style={{ background: 'rgba(8,12,239,0.15)', border: '1px solid rgba(8,12,239,0.3)', color: '#818cf8', marginBottom: 20, display: 'inline-flex' }}>
            🏢 For Companies & Hiring Partners
          </span>

          <h1 className="syne" style={{ fontSize: 'clamp(2rem,5vw,3.6rem)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.03em', marginBottom: 18 }}>
            Find World-Class Talent.<br />
            <span className="grad-text">Pay Instantly in ONT.</span>
          </h1>

          <p style={{ fontSize: 'clamp(14px,2.2vw,17px)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, marginBottom: 36, maxWidth: 600, margin: '0 auto 36px' }}>
            Oracle Nexus gives companies access to a global pool of KYC-verified talent, AI-powered matching, and blockchain-native payments — all in one platform.
          </p>

          {isTalent ? (
            /* ── Talent user: redirect them back ── */
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <div style={{ padding: '16px 24px', borderRadius: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', maxWidth: 460 }}>
                <p style={{ fontSize: 14, color: C.warning, lineHeight: 1.6 }}>
                  👋 You're signed in as <strong style={{ color: '#fff' }}>Talent</strong>. This area is for companies that want to hire. Head to your dashboard to find opportunities.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button onClick={() => navigate('/dashboard')} style={{ padding: '13px 28px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}>
                  Go to My Dashboard →
                </button>
                <button onClick={() => navigate('/opportunities')} style={{ padding: '12px 24px', background: 'transparent', border: '1px solid rgba(254,52,232,0.35)', borderRadius: 9, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Browse Opportunities
                </button>
              </div>
            </div>
          ) : (
            /* ── Guest: prompt sign-up ── */
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => openAuthModal('register')}
                style={{ padding: 'clamp(12px,2vw,15px) clamp(24px,4vw,36px)', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 10, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif", boxShadow: `0 8px 32px ${C.magenta}40` }}
              >
                Register Your Company →
              </button>
              <button
                onClick={() => openAuthModal('login')}
                style={{ padding: 'clamp(11px,2vw,14px) clamp(20px,3vw,28px)', background: 'transparent', border: '1px solid rgba(254,52,232,0.35)', borderRadius: 10, color: '#fff', fontSize: 'clamp(13px,2vw,15px)', fontWeight: 600, cursor: 'pointer' }}
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Perks grid */}
      <div className="container" style={{ padding: 'clamp(40px,6vw,72px) clamp(16px,4vw,28px)' }}>
        <div style={{ textAlign: 'center', marginBottom: 'clamp(28px,4vw,48px)' }}>
          <h2 className="syne" style={{ fontSize: 'clamp(1.5rem,3.5vw,2.4rem)', fontWeight: 800, marginBottom: 10 }}>
            Everything You Need to <span className="grad-text">Hire Better</span>
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)' }}>Built for modern companies that want speed, quality, and transparency</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(clamp(240px,30%,320px),1fr))', gap: 'clamp(12px,2vw,18px)', marginBottom: 'clamp(40px,6vw,72px)' }}>
          {PERKS.map((p, i) => (
            <div key={i} className="card" style={{ padding: 'clamp(20px,3vw,28px)' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${C.violet}80,transparent)` }} />
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(95,8,231,0.15)', border: '1px solid rgba(95,8,231,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16 }}>
                {p.icon}
              </div>
              <h3 className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{p.title}</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.48)', lineHeight: 1.65 }}>{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, padding: 'clamp(20px,3vw,32px)', borderRadius: 16, background: `linear-gradient(135deg,${C.navy}50,${C.violet}20)`, border: '1px solid rgba(95,8,231,0.2)', marginBottom: 'clamp(40px,6vw,64px)' }}>
          {[['1,200+','Companies'],['50K+','Verified Talents'],['94%','Hire Rate'],['180+','Countries']].map(([v, l]) => (
            <div key={l} style={{ textAlign: 'center', padding: '12px 0' }}>
              <div className="syne" style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 800, color: C.magenta }}>{v}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* CTA bottom — only for guests */}
        {!user && (
          <div style={{ textAlign: 'center', padding: 'clamp(32px,5vw,56px) clamp(20px,4vw,40px)', borderRadius: 20, background: `linear-gradient(135deg,${C.navy}60,${C.violet}30)`, border: '1px solid rgba(254,52,232,0.15)', position: 'relative', overflow: 'hidden' }}>
            <div className="grid-bg" style={{ opacity: 0.2 }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 className="syne" style={{ fontSize: 'clamp(1.4rem,3vw,2rem)', fontWeight: 800, marginBottom: 12 }}>
                Ready to hire the world's best talent?
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', marginBottom: 28 }}>
                Sign up as a Company — your account goes live within 24 hours after verification.
              </p>
              <button
                onClick={() => openAuthModal('register')}
                style={{ padding: 'clamp(12px,2vw,15px) clamp(28px,5vw,44px)', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 10, color: '#fff', fontSize: 'clamp(14px,2vw,16px)', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif", boxShadow: `0 8px 32px ${C.magenta}40` }}
              >
                Get Started — It's Free →
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
